import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material';
import { TradingSystem } from '@monorepo/types';
import {
  CredentialsSection,
  ErrorState,
  LoadingState,
  PlatformLoginContent,
} from './components';
import { useNovaTraderLogin } from './hooks/use-nova-trader-login';
import { NovaTraderLoginProps } from './types';

export const NovaTraderLogin: FunctionComponent<NovaTraderLoginProps> = ({
  userId,
  tradingSystem,
  accountId,
  contest,
  loading,
  externalShowPassword,
  externalTogglePasswordVisibility,
}) => {
  const { t } = useTranslation('login');
  const {
    tsUser,
    isLoading,
    error,
    showPassword: localShowPassword,
    handleCopy,
    togglePasswordVisibility: localTogglePasswordVisibility,
  } = useNovaTraderLogin({
    userId,
    tradingSystem,
    accountId,
    contest,
  });

  const showPassword = externalShowPassword ?? localShowPassword;
  const togglePasswordVisibility =
    externalTogglePasswordVisibility ?? localTogglePasswordVisibility;

  const hideInternalPasswordToggle = !!externalTogglePasswordVisibility;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState type="error" />;
  }

  if (
    tradingSystem === TradingSystem.CTrader ||
    tradingSystem === TradingSystem.BlackArrowFutures
  ) {
    return <PlatformLoginContent tradingSystem={tradingSystem} />;
  }

  if (!tsUser) {
    return <ErrorState type="no-credentials" />;
  }

  const contestPageStyles = contest
    ? (theme: Theme) => ({
        backgroundColor: theme.palette.background.blue,
        padding: theme.spacing(4, 2),
        borderRadius: theme.spacing(5),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        minHeight: theme.spacing(30),
        minWidth: 0,
        overflow: 'hidden',
      })
    : undefined;

  const credentialsTitleText = contest
    ? loading
      ? t(
          'traders_login.trading_credentials_will_be_unlocked_at_contest_start_time',
        )
      : t(
          'traders_login.here_are_your_login_details_to_access_the_contest_account_on',
        )
    : undefined;
  const platformButtonsTitleText = contest
    ? t('traders_login.download_the_platform_if_you_havent_yet')
    : undefined;

  return (
    <CredentialsSection
      tsUser={tsUser}
      tradingSystem={tradingSystem}
      showPassword={showPassword}
      togglePasswordVisibility={
        hideInternalPasswordToggle ? undefined : togglePasswordVisibility
      }
      handleCopy={handleCopy}
      credentialsSectionStyles={contestPageStyles}
      credentialsTitleText={credentialsTitleText}
      platformButtonsTitleText={platformButtonsTitleText}
      direction={contest ? 'column' : 'row'}
      isPrimaryColor={contest ?? false}
      loading={loading ?? false}
    />
  );
};
